import React, { useState } from 'react';
import { 
  BookOpen, 
  Clock, 
  Zap, 
  BrainCircuit, 
  CheckCircle2, 
  Sparkles, 
  X, 
  ArrowRight,
  ShieldCheck,
  Lock,
  Timer
} from 'lucide-react';
import { PraxisPaper, ExamMode } from '../types';

interface ExamModeSelectorModalProps {
  paper: PraxisPaper;
  isOpen: boolean;
  onClose: () => void;
  onSelectMode: (mode: ExamMode) => void;
}

interface ModeConfig {
  id: ExamMode;
  title: string;
  tagline: string;
  description: string;
  badge: string;
  icon: React.ComponentType<{ className?: string }>;
  accentColor: 'emerald' | 'indigo' | 'amber' | 'purple';
  highlights: string[];
  disabled?: boolean;
  disabledBadge?: string;
}

export const ExamModeSelectorModal: React.FC<ExamModeSelectorModalProps> = ({
  paper,
  isOpen,
  onClose,
  onSelectMode
}) => {
  const [selectedMode, setSelectedMode] = useState<ExamMode>('timed');

  if (!isOpen) return null;

  const durationMin = paper.durationMinutes || 85;

  // Mode definitions array - easily extensible (Adaptive mode card included as 4th slot)
  const MODES: ModeConfig[] = [
    {
      id: 'study',
      title: 'Study Mode',
      tagline: 'Self-paced learning & immediate rationale',
      description: 'Practice with zero time pressure. Step-by-step rationales and pedagogical explanations appear immediately after each question.',
      badge: 'No Timer • Full Question Pool',
      icon: BookOpen,
      accentColor: 'emerald',
      highlights: [
        'Immediate rationale on selection',
        'Self-paced (no auto-advance)',
        'Full question pool access'
      ]
    },
    {
      id: 'timed',
      title: 'Timed Mock Exam',
      tagline: 'Authentic standardized testing conditions',
      description: `Timed exam simulation with real ${durationMin}-minute countdown, immediate question locking, and complete diagnostic debrief upon submission.`,
      badge: `${durationMin}m Real Clock • Locked Answers`,
      icon: Clock,
      accentColor: 'indigo',
      highlights: [
        `${durationMin}-minute standardized time limit`,
        'Answers lock immediately',
        'Randomized exam sample',
        'End-of-test full score report'
      ]
    },
    {
      id: 'rapid',
      title: 'Rapid Fire',
      tagline: '45-second high-speed recall drill',
      description: 'Train fast instincts under pressure. 45-second countdown per question with automatic advance and quick snapshot rationales.',
      badge: '45s per Question • Auto-Advance',
      icon: Zap,
      accentColor: 'amber',
      highlights: [
        '45-second timer per question',
        'Auto-submits on time expiration',
        'Instant 1-sentence snapshot rationale',
        'Fast-paced recall training'
      ]
    },
    {
      id: 'adaptive',
      title: 'Adaptive Mode',
      tagline: 'Real-time dynamic difficulty pacing',
      description: 'Adapts question difficulty in real time within your session. 3 consecutive correct answers raises the difficulty tier; misses lower the tier and prioritize your weaker domains.',
      badge: 'No Timer • Real-Time Pacing',
      icon: BrainCircuit,
      accentColor: 'purple',
      highlights: [
        '3 consecutive correct raises tier',
        'Wrong answers lower tier & target weak domains',
        'No question repeats in session',
        'Self-paced with step-by-step rationales'
      ]
    }
  ];

  const handleLaunch = (mode: ExamMode) => {
    onSelectMode(mode);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-4xl w-full p-6 sm:p-8 shadow-2xl border border-slate-200 my-auto space-y-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 rounded-full text-xs font-extrabold bg-indigo-100 text-indigo-800 border border-indigo-200 font-mono">
                PRAXIS {paper.code}
              </span>
              <span className="text-xs font-bold text-slate-500">
                Mode Selection
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
              Select Your Practice Mode
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 font-medium line-clamp-1">
              {paper.title}
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
            aria-label="Close mode selector"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 4-Card Grid (Map over MODES array) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MODES.map((mode) => {
            const Icon = mode.icon;
            const isSelected = selectedMode === mode.id && !mode.disabled;
            const isDisabled = !!mode.disabled;

            let colorClasses = {
              border: 'border-slate-200 hover:border-slate-300',
              badgeBg: 'bg-slate-100 text-slate-700',
              iconBg: 'bg-slate-100 text-slate-700',
              ring: ''
            };

            if (mode.accentColor === 'emerald') {
              colorClasses = {
                border: isSelected ? 'border-emerald-500 bg-emerald-50/40' : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/20',
                badgeBg: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
                iconBg: isSelected ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700',
                ring: isSelected ? 'ring-2 ring-emerald-500/20' : ''
              };
            } else if (mode.accentColor === 'indigo') {
              colorClasses = {
                border: isSelected ? 'border-indigo-500 bg-indigo-50/40' : 'border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/20',
                badgeBg: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
                iconBg: isSelected ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700',
                ring: isSelected ? 'ring-2 ring-indigo-500/20' : ''
              };
            } else if (mode.accentColor === 'amber') {
              colorClasses = {
                border: isSelected ? 'border-amber-500 bg-amber-50/40' : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50/20',
                badgeBg: 'bg-amber-100 text-amber-800 border border-amber-200',
                iconBg: isSelected ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-700',
                ring: isSelected ? 'ring-2 ring-amber-500/20' : ''
              };
            } else if (mode.accentColor === 'purple') {
              colorClasses = {
                border: isSelected ? 'border-purple-500 bg-purple-50/40' : 'border-slate-200 hover:border-purple-300 hover:bg-purple-50/20',
                badgeBg: 'bg-purple-100 text-purple-800 border border-purple-200',
                iconBg: isSelected ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700',
                ring: isSelected ? 'ring-2 ring-purple-500/20' : ''
              };
            }

            return (
              <div
                key={mode.id}
                onClick={() => {
                  if (!isDisabled) {
                    setSelectedMode(mode.id);
                  }
                }}
                className={`rounded-2xl border-2 p-5 transition-all flex flex-col justify-between relative text-left ${
                  isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                } ${colorClasses.border} ${colorClasses.ring}`}
              >
                {isDisabled && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-slate-200 text-slate-700">
                    {mode.disabledBadge || 'Coming Soon'}
                  </span>
                )}

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 transition-colors ${colorClasses.iconBg}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                        <span>{mode.title}</span>
                        {isSelected && (
                          <CheckCircle2 className="w-4 h-4 text-indigo-600 inline" />
                        )}
                      </h3>
                      <p className="text-xs font-semibold text-slate-500">
                        {mode.tagline}
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-slate-600 leading-relaxed">
                    {mode.description}
                  </p>

                  <ul className="space-y-1.5 pt-1">
                    {mode.highlights.map((hl, hlIdx) => (
                      <li key={hlIdx} className="text-[11px] font-medium text-slate-700 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span>{hl}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4 mt-3 border-t border-slate-100/80 flex items-center justify-between gap-2">
                  <span className={`px-2.5 py-1 rounded-lg text-[11px] font-bold ${colorClasses.badgeBg}`}>
                    {mode.badge}
                  </span>

                  {!isDisabled && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLaunch(mode.id);
                      }}
                      className={`text-xs font-bold px-3.5 py-1.5 rounded-xl transition flex items-center gap-1 shadow-2xs ${
                        isSelected
                          ? 'bg-slate-900 hover:bg-black text-white'
                          : 'bg-white border border-slate-300 hover:bg-slate-50 text-slate-800'
                      }`}
                    >
                      <span>Start</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Actions */}
        <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <div className="text-slate-500 font-medium text-center sm:text-left">
            Selected: <span className="font-bold text-slate-900 uppercase">{selectedMode} Mode</span> — You can switch modes at any time from the paper directory.
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold transition"
            >
              Cancel
            </button>
            <button
              onClick={() => handleLaunch(selectedMode)}
              className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold transition shadow-md flex items-center justify-center gap-2"
            >
              <span>Launch {selectedMode.toUpperCase()} Session</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
